if (!customElements.get('qeviq-bundle-selector')) {
  customElements.define(
    'qeviq-bundle-selector',
    class QeviqBundleSelector extends HTMLElement {
      constructor() {
        super();
        this.cards = [];
        this.selectedCard = null;
        this.submitButton = null;
        this.errorEl = null;
        this.track = null;
        this.sliderPrev = null;
        this.sliderNext = null;
        this.sliderDots = [];
        this.activeSlideIndex = 0;
        this.onTrackScroll = this.onTrackScroll.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('[data-qbs-card]'));
        this.form = this.querySelector('form[data-purchase-flow="bundle"]');
        this.submitButton = this.querySelector('[data-qbs-submit]');
        this.errorEl = this.querySelector('[data-qbs-error]');
        this.trustEl = this.querySelector('[data-qbs-trust]');
        this.trustTextEl = this.querySelector('[data-qbs-trust-text]');
        this.track = this.querySelector('[data-qbs-grid]');
        this.sliderPrev = this.querySelector('[data-qbs-slider-prev]');
        this.sliderNext = this.querySelector('[data-qbs-slider-next]');
        this.sliderDots = Array.from(this.querySelectorAll('[data-qbs-slider-dot]'));

        this.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (window.QeviqSharedAddToCart) {
            if (this.hasAttribute('data-qbs-plans')) {
              this.submitSelectedPlan(event);
              return;
            }
            window.QeviqSharedAddToCart.handleBundleSubmit(this.submitButton, event);
            return;
          }
          this.onSubmit(event);
        });

        if (this.hasAttribute('data-qbs-plans')) {
          this.initRoutinePlans();
          return;
        }

        if (!this.cards.length) return;

        this.cards.forEach((card) => {
          card.addEventListener('click', (event) => this.onCardClick(event, card));
          card.addEventListener('keydown', (event) => this.onCardKeydown(event, card));
          this.initGallery(card);
        });

        const initial = this.cards.find((card) => card.classList.contains('is-selected')) || this.cards[0];
        this.selectCard(initial, false);
        this.initSlider();
      }

      initRoutinePlans() {
        this.planRoot = this.querySelector('[data-qbs-plans]');
        this.planCards = Array.from(this.querySelectorAll('[data-qbs-plan-card]'));
        this.planRadios = Array.from(this.querySelectorAll('[data-qbs-plan-radio]'));
        this.riseVariantMap = this.parseRiseVariantMap();
        this.ctaLabel = this.submitButton?.dataset.ctaLabel || 'ADD TO CART';

        this.planRadios.forEach((input) => {
          input.addEventListener('change', () => this.syncRoutinePlans());
        });

        this.planCards.forEach((card) => {
          card.addEventListener('click', (event) => {
            if (event.target.closest('a')) return;
            const input = card.querySelector('[data-qbs-plan-radio]');
            if (!input || input.checked) return;
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });

        this.onVariantChange = () => this.syncRiseVariantPrice();
        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.syncRiseVariantPrice();
        this.syncRoutinePlans();
      }

      parseRiseVariantMap() {
        const script = this.querySelector('[data-qbs-rise-variants]');
        if (!script) return {};
        try {
          const list = JSON.parse(script.textContent || '[]');
          return Object.fromEntries((list || []).map((item) => [String(item.id), Number(item.price) || 0]));
        } catch (error) {
          return {};
        }
      }

      getCurrentRisePriceCents() {
        const variantId =
          window.QeviqSharedAddToCart?.getSelectedVariantId?.() ||
          document.querySelector('product-info input[name="id"]')?.value;
        if (variantId && this.riseVariantMap[String(variantId)]) {
          return this.riseVariantMap[String(variantId)];
        }
        return parseInt(this.planRoot?.dataset.risePrice, 10) || 0;
      }

      formatRoutineMoney(cents) {
        const format = this.planRoot?.dataset.moneyFormat || this.summaryEl?.dataset.moneyFormat || '${{amount}}';
        const value = (Number(cents) || 0) / 100;
        const isWhole = Math.abs(value - Math.round(value)) < 0.001;
        const amount = isWhole ? String(Math.round(value)) : value.toFixed(2);
        const amountNoDecimals = String(Math.round(value));
        const amountWithComma = amount.replace('.', ',');
        return format
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
          .replace(/\{\{\s*amount\s*\}\}/g, amount);
      }

      syncRiseVariantPrice() {
        const riseCents = this.getCurrentRisePriceCents();
        this.planRoot?.setAttribute('data-rise-price', String(riseCents));

        this.planCards.forEach((card) => {
          if (card.dataset.priceFixed === 'true') return;
          if (card.dataset.plan !== 'rise') return;

          card.dataset.priceCents = String(riseCents);
          const priceEl = card.querySelector('[data-qbs-plan-price]');
          if (priceEl && riseCents > 0) priceEl.textContent = this.formatRoutineMoney(riseCents);
        });

        this.syncRoutinePlans();
      }

      getSelectedPlanCard() {
        const checked = this.querySelector('[data-qbs-plan-radio]:checked');
        return checked?.closest('[data-qbs-plan-card]') || this.planCards[1] || this.planCards[0];
      }

      syncRoutinePlans() {
        const selected = this.getSelectedPlanCard();
        this.planCards.forEach((card) => {
          const on = card === selected;
          card.classList.toggle('is-selected', on);
          const radio = card.querySelector('[data-qbs-plan-radio]');
          if (radio) radio.checked = on;
        });

        this.syncPlanPreview(selected?.dataset.plan || 'reset');
        this.syncMainPriceDisplay(selected);
        this.syncSubmitAvailability(selected);

        const labelEl = this.submitButton?.querySelector('[data-qeviq-atc-label]');
        if (labelEl) {
          labelEl.textContent = this.ctaLabel;
        }

        const flow = selected?.dataset.flow || 'bundle';
        this.submitButton?.setAttribute('data-purchase-flow', flow);
        this.form?.setAttribute('data-purchase-flow', flow === 'standalone' ? 'bundle' : 'bundle');

        document.dispatchEvent(
          new CustomEvent('qeviq:routine-plan-change', {
            bubbles: true,
            detail: { card: selected },
          })
        );
      }

      syncSubmitAvailability(selected) {
        if (!this.submitButton) return;
        const isBundle = selected?.dataset.flow === 'bundle';
        const bundleAvailable = selected?.dataset.bundleAvailable !== 'false';
        const shouldDisable = isBundle && !bundleAvailable;

        this.submitButton.toggleAttribute('disabled', shouldDisable);
        this.submitButton.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
      }

      syncMainPriceDisplay(selected) {
        const sectionId = this.planRoot?.dataset.sectionId;
        if (!sectionId || !selected) return;

        const priceCents = parseInt(selected.dataset.priceCents, 10) || 0;
        const compareCents = parseInt(selected.dataset.compareCents, 10) || 0;
        const savePercent = parseInt(selected.dataset.savePercent, 10) || 0;
        const onSale = compareCents > priceCents && priceCents > 0;

        const priceRoot = document.getElementById(`price-${sectionId}`);
        if (!priceRoot) return;

        const row =
          priceRoot.querySelector('.qeviq-pdp-title-price__price-row') ||
          priceRoot.querySelector('.price__container') ||
          priceRoot;

        let compareEl = row.querySelector('.qeviq-pdp-title-price__compare, .price__sale s, s.price-item');
        let priceEl = row.querySelector('.qeviq-pdp-title-price__price, .price-item--sale, .price-item--regular');
        let badgeEl = row.querySelector('.qeviq-pdp-title-price__badge');

        if (!priceEl) {
          priceRoot.innerHTML = `
            <div class="qeviq-pdp-title-price__price-row${onSale ? '' : ' qeviq-pdp-title-price__price-row--regular'}">
              <span class="qeviq-pdp-title-price__price"></span>
            </div>
          `;
          priceEl = priceRoot.querySelector('.qeviq-pdp-title-price__price');
        }

        if (priceEl && priceCents > 0) {
          priceEl.textContent = this.formatRoutineMoney(priceCents);
        }

        if (onSale) {
          row.classList.remove('qeviq-pdp-title-price__price-row--regular');
          if (!compareEl) {
            compareEl = document.createElement('s');
            compareEl.className = 'qeviq-pdp-title-price__compare';
            priceEl?.insertAdjacentElement('afterend', compareEl);
          }
          compareEl.textContent = this.formatRoutineMoney(compareCents);
          compareEl.hidden = false;
        } else if (compareEl) {
          compareEl.remove();
          compareEl = null;
          row.classList.add('qeviq-pdp-title-price__price-row--regular');
        }

        if (savePercent > 0 && onSale) {
          if (!badgeEl) {
            badgeEl = document.createElement('span');
            badgeEl.className = 'qeviq-pdp-title-price__badge';
            row.appendChild(badgeEl);
          }
          badgeEl.textContent = `SAVE ${savePercent}%`;
          badgeEl.hidden = false;
        } else if (badgeEl) {
          badgeEl.remove();
          badgeEl = null;
        }
      }

      syncPlanPreview(plan) {
        const visibleByPlan = {
          rise: ['rise'],
          reset: ['rise', 'reset'],
          complete: ['rise', 'reset', 'quiet', 'drift'],
        };
        const visible = visibleByPlan[plan] || visibleByPlan.reset;
        const preview = this.querySelector('[data-qbs-plan-preview]');
        if (!preview) return;

        preview.classList.toggle('is-complete', plan === 'complete');
        preview.classList.toggle('is-reset', plan === 'reset');
        preview.classList.toggle('is-rise', plan === 'rise');

        preview.querySelectorAll('[data-qbs-preview]').forEach((item) => {
          const on = visible.includes(item.getAttribute('data-qbs-preview'));
          item.classList.toggle('is-active', on);
          item.hidden = !on;
        });
        preview.querySelectorAll('[data-qbs-preview-plus]').forEach((plus) => {
          const on = visible.includes(plus.getAttribute('data-qbs-preview-plus'));
          plus.classList.toggle('is-active', on);
          plus.hidden = !on;
        });
      }

      submitSelectedPlan(event) {
        const selected = this.getSelectedPlanCard();
        const flow = selected?.dataset.flow || 'bundle';
        if (flow === 'standalone') {
          this.submitButton?.setAttribute('data-purchase-flow', 'standalone');
          window.QeviqSharedAddToCart.handleStandaloneClick(this.submitButton, event);
          return;
        }
        this.submitButton?.setAttribute('data-purchase-flow', 'bundle');
        selected?.classList.add('is-selected');
        window.QeviqSharedAddToCart.handleBundleSubmit(this.submitButton, event);
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onTrackScroll);
        window.removeEventListener('resize', this.onResize);
        if (typeof this.variantChangeUnsubscriber === 'function') this.variantChangeUnsubscriber();
      }

      initSlider() {
        if (!this.track) return;

        this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
        window.addEventListener('resize', this.onResize);

        this.sliderPrev?.addEventListener('click', () => this.scrollToSlide(this.activeSlideIndex - 1));
        this.sliderNext?.addEventListener('click', () => this.scrollToSlide(this.activeSlideIndex + 1));

        this.sliderDots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const index = parseInt(dot.dataset.qbsSliderDot, 10) || 0;
            this.scrollToSlide(index);
          });
        });

        requestAnimationFrame(() => {
          this.updateSliderState();
          this.scrollCardIntoView(this.selectedCard || this.cards[0], 'auto');
        });
      }

      getSlideOffsets() {
        if (!this.track || !this.cards.length) return [];

        const trackRect = this.track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;

        return this.cards.map((card) => {
          const cardRect = card.getBoundingClientRect();
          const cardCenter = cardRect.left + cardRect.width / 2;
          return cardCenter - trackCenter;
        });
      }

      getNearestSlideIndex() {
        const offsets = this.getSlideOffsets();
        if (!offsets.length) return 0;

        let nearest = 0;
        let smallest = Math.abs(offsets[0]);

        offsets.forEach((offset, index) => {
          const distance = Math.abs(offset);
          if (distance < smallest) {
            smallest = distance;
            nearest = index;
          }
        });

        return nearest;
      }

      scrollToSlide(index) {
        const card = this.cards[index];
        if (!card) return;
        this.scrollCardIntoView(card, 'smooth');
        this.selectCard(card, false);
      }

      scrollCardIntoView(card, behavior = 'smooth') {
        if (!card || !this.track) return;

        /* Scroll only the horizontal track — never the page. */
        const trackRect = this.track.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        const delta =
          cardRect.left + cardRect.width / 2 - (trackRect.left + trackRect.width / 2);
        const left = this.track.scrollLeft + delta;
        this.track.scrollTo({
          left: Math.max(0, left),
          behavior: behavior === 'smooth' ? 'smooth' : 'auto',
        });
      }

      onTrackScroll() {
        window.requestAnimationFrame(() => this.updateSliderState());
      }

      onResize() {
        window.requestAnimationFrame(() => {
          this.updateSliderState();
          if (this.selectedCard) this.scrollCardIntoView(this.selectedCard, 'auto');
        });
      }

      updateSliderState() {
        if (!this.cards.length) return;

        this.activeSlideIndex = this.getNearestSlideIndex();

        this.sliderDots.forEach((dot, index) => {
          const isActive = index === this.activeSlideIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        if (this.sliderPrev) this.sliderPrev.disabled = this.activeSlideIndex <= 0;
        if (this.sliderNext) this.sliderNext.disabled = this.activeSlideIndex >= this.cards.length - 1;
      }

      onCardClick(event, card) {
        if (event.target.closest('[data-qbs-gallery-nav], [data-qbs-gallery-dot]')) return;
        this.selectCard(card, true);
        this.scrollCardIntoView(card, 'smooth');
      }

      onCardKeydown(event, card) {
        const index = this.cards.indexOf(card);
        if (index < 0) return;

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectCard(card, true);
          this.scrollCardIntoView(card, 'smooth');
          return;
        }

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          const next = this.cards[(index + 1) % this.cards.length];
          this.selectCard(next, true);
          this.scrollToSlide(index + 1);
          next.focus();
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          const prev = this.cards[(index - 1 + this.cards.length) % this.cards.length];
          this.selectCard(prev, true);
          this.scrollToSlide(index - 1);
          prev.focus();
        }
      }

      selectCard(card, shouldFocus) {
        if (!card) return;

        this.cards.forEach((item) => {
          const isSelected = item === card;
          item.classList.toggle('is-selected', isSelected);
          item.setAttribute('aria-checked', isSelected ? 'true' : 'false');
          item.tabIndex = isSelected ? 0 : -1;

          const radio = item.querySelector('[data-qbs-radio]');
          if (radio) radio.checked = isSelected;
        });

        this.selectedCard = card;
        this.hideError();
        this.updateTrustMessage(card);
        this.dispatchEvent(
          new CustomEvent('qeviq:bundle-selected', {
            bubbles: true,
            detail: { card },
          })
        );

        if (shouldFocus) card.focus();
      }

      updateTrustMessage(card) {
        if (!this.trustEl || !this.trustTextEl) return;
        if (this.dataset.trustOverride) return;

        const message = card?.dataset.bottomMessage || '';
        this.trustTextEl.textContent = message;
        this.trustEl.classList.toggle('hidden', !message);
      }

      initGallery(card) {
        const gallery = card.querySelector('[data-qbs-gallery]');
        if (!gallery || gallery.hasAttribute('data-qbs-gallery-single')) return;

        const slides = Array.from(gallery.querySelectorAll('[data-qbs-gallery-slide]'));
        const dots = Array.from(gallery.querySelectorAll('[data-qbs-gallery-dot]'));
        const prev = gallery.querySelector('[data-qbs-gallery-prev]');
        const next = gallery.querySelector('[data-qbs-gallery-next]');

        if (slides.length < 2) return;

        let activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
        if (activeIndex < 0) activeIndex = 0;

        const setSlide = (index) => {
          activeIndex = (index + slides.length) % slides.length;
          slides.forEach((slide, i) => slide.classList.toggle('is-active', i === activeIndex));
          dots.forEach((dot, i) => {
            dot.classList.toggle('is-active', i === activeIndex);
            dot.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
          });
        };

        prev?.addEventListener('click', (event) => {
          event.stopPropagation();
          setSlide(activeIndex - 1);
        });

        next?.addEventListener('click', (event) => {
          event.stopPropagation();
          setSlide(activeIndex + 1);
        });

        dots.forEach((dot) => {
          dot.addEventListener('click', (event) => {
            event.stopPropagation();
            setSlide(parseInt(dot.dataset.qbsGalleryDot, 10) || 0);
          });
        });
      }

      getSelectedVariantId() {
        if (!this.selectedCard) {
          const fallback = this.submitButton?.dataset.defaultVariant;
          return fallback || '';
        }

        try {
          const items = JSON.parse(this.selectedCard.getAttribute('data-bundle-line-items') || '[]');
          if (Array.isArray(items) && items[0]?.id) return String(items[0].id);
        } catch (error) {
          // Fall through to legacy dataset.
        }

        return this.selectedCard.dataset.variantId || '';
      }

      hideError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.classList.add('hidden');
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.remove('hidden');
      }

      async onSubmit() {
        if (!this.selectedCard && this.cards.length) {
          this.selectCard(this.cards[0], false);
        }

        const variantId = this.getSelectedVariantId();
        if (!variantId) {
          this.showError('This bundle is not linked to a product yet.');
          return;
        }

        this.hideError();
        this.setLoading(true);

        try {
          const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');

          if (cart && typeof cart.getSectionsToRender === 'function') {
            formData.append('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
            formData.append('sections_url', window.location.pathname);
          }

          const config = {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/javascript',
            },
            body: formData,
          };

          const response = await fetch(window.routes.cart_add_url, config);
          const data = await response.json();

          if (data.status) {
            this.showError(data.description || data.message || 'Unable to add bundle to cart.');
            return;
          }

          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(data);
          } else if (window.routes?.cart_url) {
            window.location = window.routes.cart_url;
            return;
          }

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'qeviq-bundle-selector',
              productVariantId: variantId,
              cartData: data,
            });
          }
        } catch (error) {
          console.error(error);
          this.showError('Something went wrong. Please try again.');
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('is-loading', isLoading);
        this.submitButton.disabled = isLoading;
      }
    }
  );
}
